import { seasons, groups, suppliers, contactPersons, brandOptions } from './stylesFixtureData';
import type { StyleRow } from './stylesFixtureData';

/**
 * Dedicated, real persisted `stylesData` fixtures for the DL-63757 epic ("D2 Playwright
 * automation — Styles table bulk actions: selection, BulkEditModal & bulk delete", Testmo folder
 * "dl-58315", cases 621890-621928). Real rows (not an `apiTransform`-only override) so a bulk
 * PUT / bulk-delete against them round-trips through the same find-by-id/merge/persist (or
 * remove/persist) path as every other row — required for the "actual mutation, real refetch, no
 * partial application" assertions this epic's ground rules call for. Every row is searchable by a
 * unique name substring absent from every other fixture in this mock.
 *
 * Split out of `styles.ts` purely to keep that file under the repo's 800-line cap
 * (`.eslintrc.js` `max-lines`) — same pattern as `stylesBulkFixtureData.ts` / `stylesClearFieldFixtures.ts`.
 */

const baseRow = (id: number, name: string, overrides: Partial<StyleRow> = {}): StyleRow => ({
  id,
  name,
  customId: `ST-${id}`,
  description: '',
  state: 'unpublished',
  logo: '',
  season: seasons[2], // SS25 — AW26 (seasons[1]) is a genuine change; AW25 (seasons[0]) is not
  //                     selectable in the bulk-edit Season combo (documented in sibling specs).
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false,
  ...overrides,
});

/**
 * A pair reused across the submit / cancel / loading-state / selection-clear-after-edit tests
 * (`submit-cancel-loading.spec.ts`, Testmo 621894/621922/621923/621928) — each of those specs
 * gets its own fresh browser context (a new MirageJS instance reseeded from scratch), so reusing
 * the same pair across them carries no cross-test contamination.
 *
 * ids 9520x (NOT 9500x/9501x) — `stylesClearFieldFixtures.ts`'s `clearFieldInteractionStyleRows`
 * already uses id 95001/95002 for its own "ClearFieldHierarchy ParentChange A/B" rows, spliced
 * into `stylesData` BEFORE this epic's fixtures in `buildDefaultStylesData()`; a duplicate id here
 * would have `stylesData.findIndex(style => style.id === id)` silently resolve every bulk-update
 * PUT to THAT row instead of this one (Greptile catch — thanks).
 */
export const buildBulkActionsEditFixtureStyles = (): StyleRow[] => [
  baseRow(95201, 'BulkActionsFixture Edit A'),
  baseRow(95202, 'BulkActionsFixture Edit B'),
];

/**
 * Single rows with a real, non-empty brand/supplier contact already set — the "clearing" tests
 * (621912/621913) assert Apply removes it via the documented empty-GUID transform
 * (`fieldTransforms.companyContactPerson` / `.supplierContactPerson`, `config-cols.tsx`). Single-
 * item selections keep the asserted wire payload to exactly one array entry.
 */
export const buildBulkActionsContactFixtureStyles = (): StyleRow[] => [
  baseRow(95005, 'BulkActionsFixture BrandContact A'),
  baseRow(95006, 'BulkActionsFixture SupplierContact A'),
];

/** Starts with exactly one category ("Tops") so picking a different set is an unambiguous change
 * for the categories-transform test (621915). */
export const buildBulkActionsCategoriesFixtureStyles = (): StyleRow[] => [
  baseRow(95007, 'BulkActionsFixture Categories A', {
    categories: [{ id: 87, customId: 'TOP', name: 'Tops' }],
  }),
];

/** Three editable rows for the bulk-delete + post-delete-refetch tests (621895/621927) — three,
 * not two, so "select 2 of 3, delete them, the 3rd survives" is directly observable (no-partial-
 * application-in-reverse: a delete must remove exactly the targeted rows, nothing else). */
export const buildBulkActionsDeleteFixtureStyles = (): StyleRow[] => [
  baseRow(95008, 'BulkActionsFixture Delete A'),
  baseRow(95009, 'BulkActionsFixture Delete B'),
  baseRow(95010, 'BulkActionsFixture Delete C'),
];
